/**
 * ImageResultDisplay Component
 *
 * Displays generated images in a grid with download all functionality.
 */

import { useState } from 'react';
import { Download, RefreshCw, X, Clock, ImageIcon } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { getImageDownloadUrl } from '../../services/imageGenerationAPI';
import { blobToDataUrl } from '../../utils/downloadProxy';
import { isDesktopApp } from '../../utils/apiMode';
import { saveMediaFile } from '../../utils/electronBridge';
import { ImageCard } from './ImageCard';
import type { ImageResult } from '../../types';

interface ImageResultDisplayProps {
  images: ImageResult[];
  processingTime: number;
  onReset: () => void;
}

export function ImageResultDisplay({ images, processingTime, onReset }: ImageResultDisplayProps) {
  const { t } = useTranslation();
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  // Defensive: ensure images is always an array
  const safeImages = Array.isArray(images) ? images : [];

  const handleDownloadAll = async () => {
    for (let i = 0; i < safeImages.length; i++) {
      const image = safeImages[i];
      try {
        const downloadUrl = getImageDownloadUrl(image.url);
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Download proxy returned ${response.status}`);
        }
        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.startsWith('image/')) {
          throw new Error(`Unexpected content-type from proxy: ${contentType}`);
        }
        const blob = await response.blob();
        const filename = `generated-image-${i + 1}-seed-${image.seed}.png`;

        // Desktop: native save dialog (same as the sibling ImageCard). An
        // anchor click in Electron routes to Chromium's download manager,
        // whose Save-As window can open behind the app -- and firing N of
        // them in a loop buries the user in hidden dialogs.
        if (isDesktopApp()) {
          const dataUrl = await blobToDataUrl(blob);
          const saved = await saveMediaFile(dataUrl, filename);
          if (!saved.success && !saved.canceled) {
            throw new Error(saved.error ?? 'Save failed');
          }
          if (saved.canceled) break; // user dismissed the dialog: stop the batch
        } else {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        }

        // Small delay between downloads
        if (i < safeImages.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      } catch (error) {
        // Was `window.open(image.url)`: a blank window in Electron, and on the
        // web it navigates to the image instead of saving it.
        console.error(`[ImageResultDisplay] Download failed for image ${i + 1}`, error);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              {t('image.result.title', {
                count: safeImages.length,
                defaultValue: '{{count}} Images Generated',
              })}
            </h3>
            <div className="flex items-center gap-1 text-sm text-white/60">
              <Clock className="h-4 w-4" />
              <span>
                {t('image.result.processingTime', {
                  time: processingTime.toFixed(1),
                  defaultValue: 'Generated in {{time}} seconds',
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {safeImages.length > 1 && (
            <button
              type="button"
              onClick={handleDownloadAll}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              {t('image.result.downloadAll', { defaultValue: 'Download All' })}
            </button>
          )}
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 rounded-lg hover:bg-white/10 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            {t('image.result.generateNew', { defaultValue: 'Generate New' })}
          </button>
        </div>
      </div>

      {/* Image Grid - Uses @container for component-level responsiveness */}
      <div className="@container">
        <div
          className={`grid gap-3 sm:gap-4 ${
            safeImages.length === 1
              ? 'grid-cols-1 max-w-lg mx-auto'
              : safeImages.length === 2
                ? 'grid-cols-1 @sm:grid-cols-2'
                : safeImages.length === 3
                  ? 'grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-3'
                  : 'grid-cols-1 @xs:grid-cols-2 @md:grid-cols-3 @xl:grid-cols-4'
          }`}
        >
          {safeImages.map((image, index) => (
            <ImageCard
              key={`${image.seed}-${index}`}
              image={image}
              index={index}
              onZoom={setZoomedImage}
            />
          ))}
        </div>
      </div>

      {/* Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <button
            type="button"
            onClick={() => setZoomedImage(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={zoomedImage}
            alt={t('image.result.zoomedAlt', { defaultValue: 'Zoomed image' })}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default ImageResultDisplay;
